import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import $ from 'jquery';

import { ExportTable, DestroyTable, FormatDate } from '../../utils/Functions';
import Table from '../Table';
import InfoPanel from '../Panels/InfoPanel';
import iziQuestion from '../IziToast/question';
import IziToast from '../IziToast';
import UpdateCharges from './UpdateCharges';
import MiniButton from '../Buttons/MiniButton';
import PayModal from '../PayModal';
import ManualPaymentDetails from '../PayModal/ManualPaymentDetails';

const ChargesTable = ({
  isLoading,
  requestConfig,
  user,
  charges,
  hasError,
  onReload,
}) => {
  // state
  const [isReLoading, setIsReloading] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  // Invoice channel-picker modal state. We open this when the operator
  // clicks "Invoice" on a row so they can pick Email/SMS/WhatsApp per
  // dispatch instead of being locked to email-only.
  const [invoiceModalCharge, setInvoiceModalCharge] = useState(null);
  const [invoiceSendEmail, setInvoiceSendEmail] = useState(true);
  const [invoiceSendSms, setInvoiceSendSms] = useState(false);
  const [invoiceSendWhatsapp, setInvoiceSendWhatsapp] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  // ── Manual "Pay" + read-only viewer modal state ─────────────────────
  const [payRow, setPayRow] = useState(null);
  const [manualViewRow, setManualViewRow] = useState(null);

  const openPayModal = (charge) => setPayRow(charge);
  const closePayModal = () => setPayRow(null);
  const onPaySuccess = () => {
    setPayRow(null);
    onReload();
  };
  const buildPayFormData = ({
    row, amount, mode, transactionDetails, file,
  }) => {
    const fd = new FormData();
    fd.append('invoice_id', row.id);
    fd.append('amount', amount);
    fd.append('mode', mode);
    fd.append('transaction_details', transactionDetails || '');
    if (file) fd.append('proof', file);
    return fd;
  };

  // Build a stable key from the current row IDs. The `<Table>` element
  // below uses this as its React `key`, which forces a full
  // unmount/remount whenever the filtered list changes — so DataTables
  // never has to reconcile across a React row update (which is what
  // caused the `removeChild` crash on the search field). New table
  // every filter change, freshly DataTable-wrapped, no DOM ownership
  // tug-of-war between React and jQuery.
  const tableKey = React.useMemo(
    () => `tbl-${charges.length}-${charges.map((c) => c.id).join('|').slice(0, 200)}`,
    [charges],
  );

  // Initialise DataTables once per mounted table. Because `key` above
  // forces a remount on every charges change, this effect now fires on
  // a fresh <table> element every time — no destroy-then-redraw dance
  // is needed since the old DataTable was unmounted with its DOM.
  useEffect(() => {
    DestroyTable('charges_table');
    const t = window.setTimeout(() => {
      ExportTable('charges_table', 'charges');
    }, 0);
    return () => {
      window.clearTimeout(t);
      DestroyTable('charges_table');
    };
  }, [tableKey]);

  /**
   * Best-effort display name + kind for the row's payer. Tenants come from
   * `charge.tenant`; unit owners come from `charge.owner.user`; an unknown
   * non-tenant payer (e.g. record not joined locally) falls back to a
   * generic "Unit Owner" label so the prompt isn't blank.
   */
  function recipientFor(charge) {
    if (charge.tenant) {
      const { tenant } = charge;
      return {
        recipientName: `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim()
          || tenant.email || 'Tenant',
        recipientKind: 'Tenant',
      };
    }
    if (charge.owner && charge.owner.user) {
      const u = charge.owner.user;
      return {
        recipientName: `${u.first_name || ''} ${u.last_name || ''}`.trim()
          || u.email || 'Unit Owner',
        recipientKind: 'Unit Owner',
      };
    }
    if (charge.payer_type && charge.payer_type !== 'tenant') {
      return { recipientName: 'Unit Owner', recipientKind: 'Unit Owner' };
    }
    return { recipientName: '', recipientKind: 'Tenant' };
  }

  // Open the channel-picker modal for the given row. Reset checkboxes to
  // a sensible default (Email on, others off) so a quick send is one click.
  function openInvoiceModal(charge) {
    setInvoiceModalCharge(charge);
    setInvoiceSendEmail(true);
    setInvoiceSendSms(false);
    setInvoiceSendWhatsapp(false);
  }

  function closeInvoiceModal() {
    if (invoiceSending) return; // don't allow close mid-flight
    setInvoiceModalCharge(null);
  }

  async function submitInvoice() {
    const charge = invoiceModalCharge;
    if (!charge) return;
    if (!invoiceSendEmail && !invoiceSendSms && !invoiceSendWhatsapp) {
      IziToast('Pick at least one channel');
      return;
    }
    setInvoiceSending(true);
    const channels = [];
    if (invoiceSendEmail) channels.push('email');
    if (invoiceSendSms) channels.push('SMS');
    if (invoiceSendWhatsapp) channels.push('WhatsApp');
    const changeLog = [
      {
        user: {
          id: user.id,
          type: user.type,
        },
        action: `Sending invoice via ${channels.join(', ')}`,
        timestamp: Math.round(new Date().getTime() / 1000.0),
      },
    ];
    const chargesObject = {
      id: charge.id,
      send_email: invoiceSendEmail,
      send_sms: invoiceSendSms,
      send_whatsapp: invoiceSendWhatsapp,
      invoice_type: 'charges',
      changelog: changeLog,
    };
    IziToast('Sending...');
    try {
      const { data: { response } } = await axios.put(
        `${process.env.REACT_APP_API_URL}invoices`,
        chargesObject,
        requestConfig,
      );
      if (response && response.id) {
        IziToast(`Invoice sent via ${channels.join(', ')}`);
      }
      setInvoiceModalCharge(null);
      onReload();
    } catch (error) {
      const msg = (error && error.response && error.response.data && error.response.data.error)
        || (error && error.message)
        || 'An error occurred';
      IziToast(msg);
    } finally {
      setInvoiceSending(false);
    }
  }

  function onCancelInvoice(charge) {
    const { recipientName } = recipientFor(charge);
    iziQuestion(
      `Cancel invoice from <i><b>${recipientName}</b></i> ?`,
      () => {
        cancelInvoice();
      },
      undefined,
    );
    async function cancelInvoice() {
      const changeLog = [
        {
          user: {
            id: user.id,
            type: user.type,
          },
          action: 'Cancelling invoice',
          timestamp: Math.round(new Date().getTime() / 1000.0),
        },
      ];
      const chargesObject = {
        id: charge.id,
        status: 'cancelled',
        invoice_type: 'charges',
        changelog: changeLog,
      };
      IziToast('Cancelling...');
      try {
        const {
          data: { response },
        } = await axios.put(
          `${process.env.REACT_APP_API_URL}invoices`,
          chargesObject,
          requestConfig,
        );
        if (response.id) {
          IziToast('Extra charge(s) successfully Cancelled');
        }
        setIsReloading(!isReLoading);
      } catch (error) {
        if (error) {
          if (error.response) {
            if (error.response.data) {
              IziToast(error.response.data.error);
            }
          }
        } else {
          IziToast('An error occurred');
        }
      }
    }
  }

  // Channel-picker modal — overlay style (same pattern used for the Water
  // Bill / Service Charge remark modals). Rendered as a sibling so it can
  // pop over any layout below.
  const invoiceModal = invoiceModalCharge && (
    <div
      onClick={closeInvoiceModal}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: 'min(420px, 92vw)',
          borderRadius: 6,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {`Send invoice to ${recipientFor(invoiceModalCharge).recipientName}`}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            Pick the channels you want to notify on.
          </div>
        </div>
        <div style={{ padding: '14px 20px' }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={invoiceSendEmail}
              onChange={(e) => setInvoiceSendEmail(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Send Email
          </label>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={invoiceSendSms}
              onChange={(e) => setInvoiceSendSms(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Send SMS
          </label>
          <label style={{ display: 'block', fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={invoiceSendWhatsapp}
              onChange={(e) => setInvoiceSendWhatsapp(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Send WhatsApp
          </label>
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={closeInvoiceModal}
            disabled={invoiceSending}
            style={{
              border: '1px solid #ccc',
              background: '#fff',
              padding: '6px 16px',
              borderRadius: 4,
              fontSize: 13,
              cursor: invoiceSending ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submitInvoice}
            disabled={invoiceSending}
            style={{
              border: '1px solid var(--red_color, #d63835)',
              background: 'var(--red_color, #d63835)',
              color: '#fff',
              padding: '6px 16px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: invoiceSending ? 'not-allowed' : 'pointer',
            }}
          >
            {invoiceSending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );

  return isLoading ? (
    <InfoPanel message="Loading..." />
  ) : hasError ? (
    <InfoPanel message="An error occurred" hasError />
  ) : charges.length > 0 ? (
    <>
      {invoiceModal}
      <UpdateCharges
        requestConfig={requestConfig}
        reLoadTable={onReload}
        selectedCharge={selectedCharge}
      />
      <PayModal
        row={payRow}
        title="Pay Extra Charge"
        subtitle={payRow ? `${recipientFor(payRow).recipientName} — ${payRow.reference_data ? payRow.reference_data.info : ''}` : ''}
        maxAmount={payRow ? Number(payRow.balance) : null}
        endpoint={`${process.env.REACT_APP_API_URL}payments/manual`}
        requestConfig={requestConfig}
        buildFormData={buildPayFormData}
        onClose={closePayModal}
        onSuccess={onPaySuccess}
      />
      <ManualPaymentDetails
        row={manualViewRow}
        amount={manualViewRow ? manualViewRow.total_amount : null}
        onClose={() => setManualViewRow(null)}
      />
      <Table key={tableKey} tableId="charges_table" marginTop={10}>
        <thead>
          <tr>
            <th>No.</th>
            <th>Date</th>
            <th>Property</th>
            <th>Hse. Unit</th>
            <th>Recipient</th>
            <th>Charge</th>
            <th>Total Penalties</th>
            <th>Balance</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {charges.map((charge, id) => {
            let index = id;
            let propertyName = '-';
            let unitNumber = '-';
            // Renamed conceptually to "recipient" — covers tenants AND unit
            // owners. We still keep tenantName as the variable to minimize churn.
            let tenantName = '-';
            let recipientKind = '';
            // The charge label (e.g. "Garbage Collection"). Falls back to the
            // first line item's type or the payment_code if info is missing.
            let chargeLabel = '-';
            if (charge.property) {
              const { property } = charge;
              if (property) {
                propertyName = property.name;
              }
            }
            if (charge.tenant) {
              const { tenant } = charge;
              if (tenant) {
                tenantName = `${tenant.first_name} ${tenant.last_name}`;
                if (tenant.other_details) {
                  unitNumber = tenant.other_details.unit_number || '';
                }
                recipientKind = 'Tenant';
              }
            } else if (charge.owner && charge.owner.user) {
              const { owner } = charge;
              const u = owner.user;
              tenantName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Unit Owner';
              if (Array.isArray(owner.unit_numbers) && owner.unit_numbers.length > 0) {
                unitNumber = owner.unit_numbers
                  .map((it) => (typeof it === 'string' ? it : (it && it.number) || ''))
                  .filter(Boolean)
                  .join(', ');
              }
              recipientKind = 'Unit Owner';
            } else if (charge.payer_type && charge.payer_type !== 'tenant') {
              // Owner record missing locally (e.g. cross-property) — show the
              // raw type so the row isn't anonymous.
              tenantName = 'Unit Owner';
              recipientKind = 'Unit Owner';
            }
            if (charge.reference_data) {
              const { reference_data: referenceData } = charge;
              if (referenceData.info) {
                chargeLabel = referenceData.info;
              } else if (referenceData.payment_code) {
                chargeLabel = referenceData.payment_code;
              }
            }
            if (chargeLabel === '-' && Array.isArray(charge.charges) && charge.charges[0]) {
              chargeLabel = charge.charges[0].type || '-';
            }
            return (
              <tr key={charge.id}>
                <td>{++index}</td>
                <td>{FormatDate(charge.invoice_date)}</td>
                <td>{propertyName}</td>
                <td>{unitNumber}</td>
                <td>
                  {tenantName}
                  {recipientKind === 'Unit Owner' && (
                    <span
                      style={{
                        marginLeft: 6,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: '#e7f5ff',
                        color: '#1864ab',
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      OWNER
                    </span>
                  )}
                </td>
                <td>{chargeLabel}</td>
                <td>{charge.total_amount}</td>
                <td>{charge.balance}</td>
                <td>{charge.status}</td>
                <td>
                  <MiniButton
                    caption="Invoice"
                    onClick={() => {
                      openInvoiceModal(charge);
                    }}
                    buttonType="danger"
                  />
                  <MiniButton
                    caption="Edit"
                    onClick={() => {
                      setSelectedCharge(charge);
                      $('#chargesModal').modal('show');
                    }}
                    buttonType="info"
                  />
                  <MiniButton
                    caption="Cancel"
                    onClick={() => {
                      onCancelInvoice(charge);
                    }}
                    buttonType=""
                  />
                  {Number(charge.balance) > 0 && charge.status !== 'cancelled' && (
                    <button
                      type="button"
                      className="btn btn-xs"
                      title="Record a manual payment for this charge"
                      style={{ color: '#27ae60', minWidth: 50, marginLeft: 2 }}
                      onClick={() => openPayModal(charge)}
                    >
                      <i className="fa fa-money" style={{ marginRight: 4 }} />
                      Pay
                    </button>
                  )}
                  {charge.status === 'paid' && charge.payment_channel === 'manual' && (
                    <button
                      type="button"
                      className="btn btn-xs"
                      title="View manual payment details"
                      style={{ color: '#2980b9', minWidth: 50, marginLeft: 2 }}
                      onClick={() => setManualViewRow(charge)}
                    >
                      <i className="fa fa-eye" style={{ marginRight: 4 }} />
                      Manual
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-xs"
                    title="View PDF invoice"
                    style={{
                      color: '#d73836',
                      minWidth: 50,
                      marginLeft: 2,
                    }}
                    onClick={() => {
                      const ref = `INV-${String(charge.id).substring(0, 10).toUpperCase()}`;
                      const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                      window.open(`${base}/public/invoice.pdf?ref=${ref}`, '_blank');
                    }}
                  >
                    <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                    PDF
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  ) : (
    <InfoPanel message="Seems no transaction found" />
  );
};

ChargesTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  requestConfig: PropTypes.objectOf(PropTypes.object),
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  onReload: PropTypes.func.isRequired,
  charges: PropTypes.arrayOf(PropTypes.object).isRequired,
};

ChargesTable.defaultProps = {
  requestConfig: {},
};

export default connect((state) => {
  return state;
})(ChargesTable);
